import { Box, createStyles, Group, Space, Stack } from '@mantine/core';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { forwardRef } from 'react';
import { Text } from '../Text';

const useStyles = createStyles(
	(theme, { selected }: { selected?: boolean }) => ({
		wrapper: {
			padding: theme.spacing.xs,
			borderRadius: theme.radius.sm,
			backgroundColor: selected
				? theme.other.getColor('fill/transparent/hover')
				: theme.other.getColor('surface/primary/default'),
			'&:hover': {
				backgroundColor: theme.other.getColor('fill/transparent/hover'),
			},
			'&:active': {
				backgroundColor: theme.other.getColor('surface/primary/active'),
			},
		},
		icon: {
			flexShrink: 0,
			color: theme.other.getColor('icon/primary/default'),
		},
		rightIcon: {
			color: theme.other.getColor('icon/secondary/default'),
		},
		grow: {
			flexGrow: 1,
		},
		title: {
			color: theme.other.getColor('text/primary/default'),
			fontWeight: theme.other.typography.weight.semibold,
			fontSize: theme.other.typography.text.sm,
			lineHeight: theme.other.typography.lineHeight.text.sm,
		},
		breadcrumb: {
			color: theme.other.getColor('text/secondary/default'),
			fontSize: theme.other.typography.text.xs,
			lineHeight: theme.other.typography.lineHeight.text.xs,
		},
	})
);

const EntityComponent = forwardRef<
	HTMLDivElement,
	ComponentPropsWithoutRef<'div'> & {
		label: ReactNode;
		icon: ReactNode;
		breadcrumbText?: string;
		'data-disabled'?: boolean;
		'data-hovered'?: boolean;
		'data-selected'?: boolean;
	}
>((props, ref) => {
	const { classes } = useStyles({ selected: props['data-selected'] });
	const {
		label,
		icon,
		id,
		className,
		onMouseEnter,
		onMouseDown,
		breadcrumbText,
	} = props;

	return (
		<div
			data-testid={`select-item-${label}`}
			ref={ref}
			key={id}
			className={className}
			onMouseDown={onMouseDown}
			onMouseEnter={onMouseEnter}
			data-disabled={props['data-disabled']}
			data-hovered={props['data-hovered']}
			data-selected={props['data-selected']}
		>
			<Group position="apart" noWrap>
				<Group spacing="xs" noWrap align="flex-start">
					{<Box className={classes.icon}>{icon}</Box>}
					<Stack spacing={4} className={classes.grow}>
						<Text className={classes.title} lineClamp={1}>
							{label ?? 'Untitled'}
						</Text>
						{breadcrumbText ? (
							<Text className={classes.breadcrumb} lineClamp={1}>
								{breadcrumbText}
							</Text>
						) : (
							<Space h="xs" />
						)}
					</Stack>
				</Group>
			</Group>
		</div>
	);
});
EntityComponent.displayName = 'EntityComponent';

export default EntityComponent;
